<template>
  <!-- Mobile trigger (mobile only) -->
  <div class="lg:hidden flex items-center px-1">
    <!-- Left side -->

    <div
      v-if="shouldShowClearAll"
      class="underline ml-[0.7143rem] text-ash-500 font-normal hover:text-gold-300 cursor-pointer text-md p-4"
      aria-label="Clear all filters"
      @click="onClearAll"
    >
      Clear All
    </div>

    <!-- Right side -->
    <Button
      id="open-filter-panel-btn"
      title="Open filters panel"
      aria-label="Open filters panel"
      type="button"
      theme="DARK"
      size="md"
      @click="openDrawer"
      class="ml-auto h-[3.5714rem] flex items-center !font-normal text-gray-900 hover:text-blue-800 focus:outline-none"
    >
      <span class="mr-2">
        <Icon icon="sliders-h" />
      </span>
      <span class="text-ash-100">Filter</span>
    </Button>
  </div>
  <hr class="border-t border-gray-300" aria-hidden="true">
  <!-- Full-screen mobile drawer -->
  <div v-if="isOpen" class="lg:hidden h-full flex flex-col">
    <div class="bg-white flex-1 flex flex-col mobile-filter">
      <div class="flex-1 px-[1.4286rem] py-3 space-y-3">
        <MobileFilterCategory
          v-for="facetKey in visibleFacets"
          :key="facetKey"
          :title="facetKey"
          :filter-key="facetKey"
          :options="facets[facetKey]?.values.map(v => v.value)??[]"
          :selected-values="selectedFiltersValue[facetKey] ?? []"
          :is-active="activeCategoryKey === facetKey"
          :selected-count="(selectedFiltersValue[facetKey] ?? []).length"
          :is-sort-by="facetKey === 'Sort By'"
          @open="onOpenCategory"
          @select="onSelectOption"
        />
      </div>

      <div class="w-full">
        <Sort
          v-model="selectedSort"
          :options="options"
          @sort-change="handleSortChange"
        />
      </div>

      <!-- Done button -->
      <div class="px-4 py-3 border-t border-gray-300">
        <Button
          id="confirm-filter-selection-btn"
          title="Confirm selection"
          aria-label="Confirm selection"
          type="button"
          theme="LIGHT"
          size="md"
          class="w-full rounded-[0.4286rem] font-normal h-[3.5714rem] bg-ash-500"
          @click.stop="onDone"
        >
          Done
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import Icon from '../../atom/icon.vue'
import Button from '../../atom/button.vue'
import MobileFilterCategory from '../../organism/product/mobileFilterCategory.vue'
import type {Facet, FacetDict, FacetsValue, SortOption} from '~~/shared/types/common'
import { PRODUCT_SORT_OPTIONS } from '~/components/utils/sortOptions'
import Sort from '../../organism/product/sort.vue'
import { URL_CONFIG } from '~~/shared/utils/url.const'

const route = useRoute()

const props = withDefaults(defineProps<{
  facets: FacetDict
  selectedFilters: Record<string, string[]>
  facetsUiVersion: number
  error: string | null
  setFacetValues: (facetName: string, values: string[]) => void
  clearAllFacets: () => void
  sortParams: { sort: string } | null
  setSortParams: (sort: string) => void
  options?: SortOption[]
}>(), {
  options: () => PRODUCT_SORT_OPTIONS
})

const visibleFacets = computed(()=> {
  const facetList: FacetDict = props.facets
  return Object.keys(facetList).filter(facetKey => {
    let containsValuesWithCount = false
    const facet: Facet | undefined = facetList[facetKey]
    const facetValues: FacetsValue[]|undefined = facet?.values

    if (facetValues) {
      for (let value of facetValues) {
        containsValuesWithCount = value?.count > 0
      }
    }

    return containsValuesWithCount
  });
})

const emit = defineEmits<{
  (e: 'update:filters', value: Record<string, string[]>): void
  (e: 'clear-all'): void
}>()

const selectedFiltersValue = computed(() => props.selectedFilters ?? {})

const isOpen = ref(false)
const activeCategoryKey = ref<string | null>(null)

// NOSONAR: Safe regex, only strips trailing slashes
const normalize = (p: string) => p.replace(/\/+$/, "")
const BASE = normalize(URL_CONFIG.PRODUCT_BASE_URL)

const isBeyondBaseProducts = computed(() => normalize(route.path) !== BASE)

const hasFacetQuery = computed(() => {
  const keys = Object.keys(route.query)
  return keys.some(k => /^f\[/i.test(k))
})

const isNavigating = ref(false)
const shouldShowClearAll = ref(false)

let debounceTimer: any = null
const setDebounced = (val: boolean) => {
  if (debounceTimer) clearTimeout(debounceTimer)

  debounceTimer = setTimeout(() => {
    shouldShowClearAll.value = val
  }, 80)
}

watch(
  [isBeyondBaseProducts, hasFacetQuery, isNavigating],
  ([beyond, facet, nav]) => {
    if (nav) {
      shouldShowClearAll.value = false
      return
    }
    setDebounced(beyond || facet)
  },
  { immediate: true }
)

const onClearAll = async () => {
  isNavigating.value = true

  await props.clearAllFacets()
  emit('update:filters', {})
  emit('clear-all')

  const current = normalize(route.path)

  if (current !== BASE) {
    window.location.replace(BASE)
    return
  }

  await nextTick()
  isOpen.value = false

  setTimeout(() => {
    isNavigating.value = false
  }, 50)
}

const openDrawer = () => {
  isOpen.value = true
}

const onOpenCategory = (key: string | null) => {
  activeCategoryKey.value = activeCategoryKey.value === key ? null : key
}

const onSelectOption = (payload: { filterKey: string; value: string }) => {
  const current = Array.isArray(selectedFiltersValue.value[payload.filterKey])
    ? [...selectedFiltersValue.value[payload.filterKey]!]
    : []
  const exists = current.includes(payload.value)
  const next = exists ? current.filter(v => v !== payload.value) : [...current, payload.value]

  props.setFacetValues(payload.filterKey, next)
  isOpen.value = false
}

const onDone = () => {
  activeCategoryKey.value = null
  isOpen.value = false
}

const options = computed(() => props.options ?? PRODUCT_SORT_OPTIONS)
const selectedSort = ref(options.value[0]!.value)

const syncSelectedFromProps = () => {
  if (!props.sortParams) {
    selectedSort.value = options.value[0]!.value
    return
  }
  const opt = options.value.find(option => option.sortp === props.sortParams!.sort)
  if (opt) selectedSort.value = opt.value
}

const handleSortChange = (sortParamsToSet: { sort: string}) => {
  props.setSortParams(sortParamsToSet.sort)
  isOpen.value = false
}

watch(() => props.sortParams, syncSelectedFromProps, { deep: true })
onMounted(syncSelectedFromProps)

watch(
  () => props.selectedFilters,
  (val) => {
    const normalized: Record<string, string[]> = {}
    Object.entries(val ?? {}).forEach(([k, v]) => {
      normalized[k] = Array.isArray(v) ? v : []
    })
    emit('update:filters', normalized)
  },
  { deep: true, immediate: true }
)
</script>
